/**
 * Logbook Entry Form Page
 * 
 * Independent page for creating or editing logbook entries.
 */

import * as React from "react";
import { useLocation, useRoute } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import type { CreateLogbookEntryPayload, Band, Mode, QSLStatus, LogbookEntry } from "@/services/logbookService";
import { userService } from "@/services/userService";
import { toast } from "sonner";
import { useCreateLogbookEntry, useUpdateLogbookEntry, useLogbookEntry } from "./hooks/useLogbookEntries";
import { useAuth } from "@/contexts/AuthContext";

const BANDS: Band[] = ["160M", "80M", "40M", "30M", "17M", "20M", "15M", "12M", "10M", "6M", "2M", "70CM", "OTHER"];
const MODES: Mode[] = ["SSB", "CW", "FT8", "FT4", "PSK31", "PSK63", "RTTY", "AM", "FM", "DIGITAL", "OTHER"];
const QSL_STATUSES: QSLStatus[] = ["NONE", "SENT", "RECEIVED", "CONFIRMED"];

export function LogbookEntryFormPage() {
  const [, setLocation] = useLocation();
  const [match, params] = useRoute<{ id: string }>("/logbook/:id/edit");
  const { user } = useAuth();
  const { createEntry, creating } = useCreateLogbookEntry();
  const { updateEntry, updating } = useUpdateLogbookEntry();

  const isEditMode = !!match && !!params;
  const entryId = params?.id;
  const { entry: existingEntry, loading: loadingEntry } = useLogbookEntry(entryId);
  const isLoading = creating || updating || loadingEntry;

  // Basic QSO Info
  const [date, setDate] = React.useState("");
  const [timeOn, setTimeOn] = React.useState("");
  const [timeOff, setTimeOff] = React.useState("");
  const [callsign, setCallsign] = React.useState("");
  const [operator, setOperator] = React.useState("");
  const [searchingCallsign, setSearchingCallsign] = React.useState(false);
  const [callsignFound, setCallsignFound] = React.useState<boolean | null>(null);

  // Frequency and Band
  const [frequency, setFrequency] = React.useState("");
  const [band, setBand] = React.useState<Band>("20M");
  const [mode, setMode] = React.useState<Mode>("SSB");

  // Signal Report
  const [rstSent, setRstSent] = React.useState("");
  const [rstReceived, setRstReceived] = React.useState("");

  // Power and Equipment
  const [power, setPower] = React.useState("");
  const [antenna, setAntenna] = React.useState("");
  const [equipment, setEquipment] = React.useState("");

  // Location & DX Info
  const [gridSquare, setGridSquare] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [state, setState] = React.useState("");
  const [dxcc, setDxcc] = React.useState("");
  const [cqZone, setCqZone] = React.useState("");
  const [ituZone, setItuZone] = React.useState("");
  const [distance, setDistance] = React.useState("");
  const [bearing, setBearing] = React.useState("");

  // QSL Information
  const [qslStatus, setQslStatus] = React.useState<QSLStatus>("NONE");
  const [qslSentDate, setQslSentDate] = React.useState("");
  const [qslReceivedDate, setQslReceivedDate] = React.useState("");
  const [qslVia, setQslVia] = React.useState("");

  // Contest and Notes
  const [contestName, setContestName] = React.useState("");
  const [notes, setNotes] = React.useState("");

  // Initialize with current date/time
  React.useEffect(() => {
    if (!date) {
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];
      if (dateStr) {
        setDate(dateStr);
      }
      const hours = String(now.getUTCHours()).padStart(2, "0");
      const minutes = String(now.getUTCMinutes()).padStart(2, "0");
      setTimeOn(`${hours}${minutes}`);
    }
  }, [date]);

  // Load entry data if editing
  React.useEffect(() => {
    if (existingEntry) {
      // Format date for date input (YYYY-MM-DD)
      const dateStr = existingEntry.date ? new Date(existingEntry.date).toISOString().split("T")[0] : "";
      if (dateStr) {
        setDate(dateStr);
      }

      // Format time (HHMM)
      if (existingEntry.timeOn) {
        setTimeOn(existingEntry.timeOn);
      }
      if (existingEntry.timeOff) {
        setTimeOff(existingEntry.timeOff);
      }
      if (existingEntry.callsign) {
        setCallsign(existingEntry.callsign);
      }
      if (existingEntry.operator) {
        setOperator(existingEntry.operator);
      }
      if (existingEntry.frequency !== undefined) {
        setFrequency(existingEntry.frequency.toString());
      }
      if (existingEntry.band) {
        setBand(existingEntry.band);
      }
      if (existingEntry.mode) {
        setMode(existingEntry.mode);
      }
      if (existingEntry.rstSent) {
        setRstSent(existingEntry.rstSent);
      }
      if (existingEntry.rstReceived) {
        setRstReceived(existingEntry.rstReceived);
      }
      if (existingEntry.power !== undefined) {
        setPower(existingEntry.power.toString());
      }
      if (existingEntry.antenna) {
        setAntenna(existingEntry.antenna);
      }
      if (existingEntry.equipment) {
        setEquipment(existingEntry.equipment);
      }
      if (existingEntry.gridSquare) {
        setGridSquare(existingEntry.gridSquare);
      }
      if (existingEntry.country) {
        setCountry(existingEntry.country);
      }
      if (existingEntry.state) {
        setState(existingEntry.state);
      }
      if (existingEntry.dxcc !== undefined) {
        setDxcc(existingEntry.dxcc.toString());
      }
      if (existingEntry.cqZone !== undefined) {
        setCqZone(existingEntry.cqZone.toString());
      }
      if (existingEntry.ituZone !== undefined) {
        setItuZone(existingEntry.ituZone.toString());
      }
      if (existingEntry.distance !== undefined) {
        setDistance(existingEntry.distance.toString());
      }
      if (existingEntry.bearing !== undefined) {
        setBearing(existingEntry.bearing.toString());
      }
      if (existingEntry.qslStatus) {
        setQslStatus(existingEntry.qslStatus);
      }
      if (existingEntry.qslSentDate) {
        const sentDateStr = new Date(existingEntry.qslSentDate).toISOString().split("T")[0];
        if (sentDateStr) {
          setQslSentDate(sentDateStr);
        }
      }
      if (existingEntry.qslReceivedDate) {
        const receivedDateStr = new Date(existingEntry.qslReceivedDate).toISOString().split("T")[0];
        if (receivedDateStr) {
          setQslReceivedDate(receivedDateStr);
        }
      }
      if (existingEntry.qslVia) {
        setQslVia(existingEntry.qslVia);
      }
      if (existingEntry.contestName) {
        setContestName(existingEntry.contestName);
      }
      if (existingEntry.notes) {
        setNotes(existingEntry.notes);
      }
    }
  }, [existingEntry]);

  // Search for callsign in UserV2
  const handleSearchCallsign = async () => {
    if (!callsign.trim()) {
      return;
    }

    setSearchingCallsign(true);
    setCallsignFound(null);

    try {
      const user = await userService.fetchUserByCallsign(callsign.trim());
      if (user) {
        setOperator(user.nama_lengkap || user.name || "");
        setCallsignFound(true);
        toast.success("Callsign found", {
          description: `Operator: ${user.nama_lengkap || user.name}`,
        });
      } else {
        setCallsignFound(false);
      }
    } catch (err) {
      setCallsignFound(false);
      const errorMessage = err instanceof Error ? err.message : "Failed to search callsign";
      if (!errorMessage.includes("404") && !errorMessage.includes("not found")) {
        toast.error("Search failed", {
          description: errorMessage,
        });
      }
    } finally {
      setSearchingCallsign(false);
    }
  };

  // Auto-search when callsign changes (debounced)
  const callsignRef = React.useRef(callsign);
  React.useEffect(() => {
    callsignRef.current = callsign;
  }, [callsign]);

  React.useEffect(() => {
    if (!callsign.trim() || callsign.trim().length < 3) {
      setCallsignFound(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      if (callsignRef.current === callsign && callsign.trim().length >= 3) {
        handleSearchCallsign();
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [callsign]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !timeOn || !callsign || !band || !mode) {
      toast.error("Please fill in all required fields");
      return;
    }

    const payload: CreateLogbookEntryPayload = {
      date,
      timeOn,
      timeOff: timeOff || undefined,
      callsign: callsign.toUpperCase().trim(),
      operator: operator || undefined,
      frequency: frequency ? parseFloat(frequency) : undefined,
      band,
      mode,
      rstSent: rstSent || undefined,
      rstReceived: rstReceived || undefined,
      power: power ? parseFloat(power) : undefined,
      antenna: antenna || undefined,
      equipment: equipment || undefined,
      gridSquare: gridSquare.toUpperCase().trim() || undefined,
      country: country || undefined,
      state: state || undefined,
      dxcc: dxcc ? parseInt(dxcc, 10) : undefined,
      cqZone: cqZone ? parseInt(cqZone, 10) : undefined,
      ituZone: ituZone ? parseInt(ituZone, 10) : undefined,
      distance: distance ? parseFloat(distance) : undefined,
      bearing: bearing ? parseFloat(bearing) : undefined,
      qslStatus,
      qslSentDate: qslSentDate || undefined,
      qslReceivedDate: qslReceivedDate || undefined,
      qslVia: qslVia || undefined,
      contestName: contestName || undefined,
      notes: notes || undefined,
    };

    try {
      if (isEditMode && entryId) {
        await updateEntry(entryId, payload);
      } else {
        await createEntry(payload);
      }
      // Navigate back to logbook page
      setLocation("/logbook");
    } catch (err) {
      // Error is handled in the hook
    }
  };

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/logbook")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {isEditMode ? "Edit Logbook Entry" : "Create Logbook Entry"}
          </h2>
          <p className="text-muted-foreground">
            {isEditMode ? "Update your logbook entry details" : "Enter QSO details. Fields marked with * are required."}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic QSO Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic QSO Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeOn">Time On (UTC) *</Label>
                  <Input
                    id="timeOn"
                    placeholder="1430"
                    value={timeOn}
                    onChange={(e) => setTimeOn(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    disabled={isLoading}
                    required
                  />
                  <p className="text-xs text-muted-foreground">Format: HHMM (24h)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeOff">Time Off (UTC)</Label>
                  <Input
                    id="timeOff"
                    placeholder="1445"
                    value={timeOff}
                    onChange={(e) => setTimeOff(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="callsign">Callsign *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        id="callsign"
                        placeholder="YB0DX"
                        value={callsign}
                        onChange={(e) => {
                          const newCallsign = e.target.value.toUpperCase();
                          setCallsign(newCallsign);
                          setCallsignFound(null);
                        }}
                        disabled={isLoading}
                        className="font-mono pr-8"
                        required
                      />
                      {callsignFound === true && (
                        <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600" />
                      )}
                      {callsignFound === false && (
                        <XCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleSearchCallsign}
                      disabled={isLoading || searchingCallsign || !callsign.trim()}
                    >
                      {searchingCallsign ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {callsignFound === true && (
                    <p className="text-xs text-green-600">Callsign found in database</p>
                  )}
                  {callsignFound === false && (
                    <p className="text-xs text-muted-foreground">
                      Callsign not found in system (normal for overseas callsigns) - enter operator name below
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="operator">Operator</Label>
                  <Input
                    id="operator"
                    placeholder="Operator name"
                    value={operator}
                    onChange={(e) => setOperator(e.target.value)}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    {callsignFound === true
                      ? "Auto-filled from database"
                      : callsignFound === false
                      ? "Enter operator name (required for overseas/unregistered callsigns)"
                      : "Enter operator name (optional if callsign found in system)"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Frequency and Band */}
          <Card>
            <CardHeader>
              <CardTitle>Frequency and Band</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency (kHz)</Label>
                  <Input
                    id="frequency"
                    type="number"
                    placeholder="14200"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="band">Band *</Label>
                  <Select value={band} onValueChange={(v) => setBand(v as Band)} disabled={isLoading}>
                    <SelectTrigger id="band">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BANDS.map((b) => (
                        <SelectItem key={b} value={b}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mode">Mode *</Label>
                  <Select value={mode} onValueChange={(v) => setMode(v as Mode)} disabled={isLoading}>
                    <SelectTrigger id="mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODES.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signal Report */}
          <Card>
            <CardHeader>
              <CardTitle>Signal Report</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="rstSent">RST Sent</Label>
                  <Input
                    id="rstSent"
                    placeholder="59"
                    value={rstSent}
                    onChange={(e) => setRstSent(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rstReceived">RST Received</Label>
                  <Input
                    id="rstReceived"
                    placeholder="59"
                    value={rstReceived}
                    onChange={(e) => setRstReceived(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Power and Equipment */}
          <Card>
            <CardHeader>
              <CardTitle>Power and Equipment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="power">Power (W)</Label>
                  <Input
                    id="power"
                    type="number"
                    placeholder="100"
                    value={power}
                    onChange={(e) => setPower(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="antenna">Antenna</Label>
                  <Input
                    id="antenna"
                    placeholder="Dipole"
                    value={antenna}
                    onChange={(e) => setAntenna(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="equipment">Equipment/Rig</Label>
                  <Input
                    id="equipment"
                    placeholder="IC-7300"
                    value={equipment}
                    onChange={(e) => setEquipment(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location & DX Info */}
          <Card>
            <CardHeader>
              <CardTitle>Location & DX Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="gridSquare">Grid Square</Label>
                  <Input
                    id="gridSquare"
                    placeholder="JI09AB"
                    value={gridSquare}
                    onChange={(e) => setGridSquare(e.target.value.toUpperCase().slice(0, 6))}
                    disabled={isLoading}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">Maidenhead format</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    placeholder="Indonesia"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State/Province</Label>
                  <Input
                    id="state"
                    placeholder="Jawa Barat"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="dxcc">DXCC Entity</Label>
                  <Input
                    id="dxcc"
                    type="number"
                    placeholder="327"
                    value={dxcc}
                    onChange={(e) => setDxcc(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cqZone">CQ Zone</Label>
                  <Input
                    id="cqZone"
                    type="number"
                    placeholder="28"
                    value={cqZone}
                    onChange={(e) => setCqZone(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ituZone">ITU Zone</Label>
                  <Input
                    id="ituZone"
                    type="number"
                    placeholder="54"
                    value={ituZone}
                    onChange={(e) => setItuZone(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="distance">Distance (km)</Label>
                  <Input
                    id="distance"
                    type="number"
                    placeholder="5000"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bearing">Bearing (degrees)</Label>
                  <Input
                    id="bearing"
                    type="number"
                    placeholder="045"
                    value={bearing}
                    onChange={(e) => setBearing(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* QSL Information */}
          <Card>
            <CardHeader>
              <CardTitle>QSL Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="qslStatus">QSL Status</Label>
                  <Select
                    value={qslStatus}
                    onValueChange={(v) => setQslStatus(v as QSLStatus)}
                    disabled={isLoading}
                  >
                    <SelectTrigger id="qslStatus">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QSL_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qslVia">QSL Via</Label>
                  <Input
                    id="qslVia"
                    placeholder="BUREAU, DIRECT, EQSL, LOTW"
                    value={qslVia}
                    onChange={(e) => setQslVia(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="qslSentDate">QSL Sent Date</Label>
                  <Input
                    id="qslSentDate"
                    type="date"
                    value={qslSentDate}
                    onChange={(e) => setQslSentDate(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qslReceivedDate">QSL Received Date</Label>
                  <Input
                    id="qslReceivedDate"
                    type="date"
                    value={qslReceivedDate}
                    onChange={(e) => setQslReceivedDate(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contest and Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Contest and Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contestName">Contest Name</Label>
                <Input
                  id="contestName"
                  placeholder="CQ WW DX Contest"
                  value={contestName}
                  onChange={(e) => setContestName(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Additional notes or comments..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isLoading}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLocation("/logbook")}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !date || !timeOn || !callsign || !band || !mode}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? "Updating..." : "Creating..."}
                </>
              ) : (
                isEditMode ? "Update Entry" : "Create Entry"
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

